import { observable, toJS } from "mobx";
import {
  starterProperties,
  ItemTypes,
  Labels,
  defaultCanvas,
  propertyDefault,
  starterSlideProperties,
  CollectionProps,
  ProgressBarStarterProperties
} from "../constants/elements";
import R from "ramda";
import uuid from "uuid/v1";
import * as mobx from "mobx";
import _ from "lodash";

import { getAspectRatio, loadGoogleFonts } from "../reusable/utils";
import user, { syncBlocks, createScene } from "../user/index";
import { RuleComparator } from "../spreadsheet/linker";

const mapPath = R.curry((path, f, obj) => R.assocPath(path, f(R.path(path, obj)), obj));

var blocks = observable.map(defaultCanvas);

const emptyTimeTravel = {
  future: [],
  history: []
};

var timeTravel = Object.assign({}, emptyTimeTravel);

var store = observable({
  blocks: blocks,
  selected: "canvas",
  canvasSize: { width: 600, height: 600 },
  showOutline: false,
  preview: false,
  get selectedBlock() {
    return this.blocks.get(this.selected);
  },
  get blocksJS() {
    return mobx.toJS(this.blocks);
  },
  get blocksTree() {
    return R.map(R.pick(["id", "label", "children", "type"]), mobx.toJS(this.blocks));
  },
  get blockIds() {
    return [...this.blocks.keys()];
  }
});

/********************************* SETTERS *********************************
 * *************************************************************************/
const ensureKey = (key, defo) => block => ({
  ...block,
  ...{ [key]: block[key] === undefined ? defo : block[key] }
});
const resizer = b => resizeBlock(b, store.canvasSize.width);
const ignoreManualSlideLive = (b, isLive) =>
  b.type === ItemTypes.ELEMENT_SLIDER && isLive && blocks.get(b.id)
    ? { ...b, slide: blocks.get(b.id).slide }
    : b;
const transformers = [
  ensureKey("rules", []),
  ensureKey("children", []),
  ensureKey("limit", CollectionProps.limit),
  ensureKey("skip", CollectionProps.skip),
  ensureKey("_skip", 0),
  ensureKey("maximum", ProgressBarStarterProperties.maximum),
  resizer,
  ignoreManualSlideLive
];
const loadAllFonts = blocks =>
  loadGoogleFonts(
    Array.from(
      _.reduce(blocks, (acc, v, k) => (v.fontFamily ? acc.add(v.fontFamily) : acc), new Set())
    )
  );

const applyTransformers = (bs, isLive) =>
  _.mapValues(bs, b => transformers.reduce((acc, f) => f(acc, isLive), b));

export const setBlocks = mobx.action("setBlocks", (bs, isLive) => {
  const adapted = applyTransformers(toJS(bs), isLive);
  const blocksJS = store.blocksJS;
  if (!_.isEqual(adapted, blocksJS)) {
    // _.forEach(adapted, (blockProps, block) => {
    //   _.forEach(blockProps, (v, prop) => {
    //     !_.isEqual(v, blocksJS[block] && blocksJS[block][prop]) &&
    //       console.log(prop, v, blocksJS[block] && blocksJS[block][prop]);
    //   });
    // });
    // console.log("inside");
    blocks.clear();
    blocks.merge(adapted);
    loadAllFonts(adapted);
  }
});

export const resetBlocks = mobx.action("resetBlocks", () => {
  blocks.clear();
  blocks.merge(defaultCanvas);
  store.selected = "canvas";
  timeTravel = emptyTimeTravel;
});

export const updateCanvasSize = mobx.action("updateCanvasSize", s => {
  store.canvasSize = s;
  const adapted = applyTransformers(user.sceneBlocks);
  blocks.merge(adapted);
});

/****************************** TimeTravel *******************************
 *************************************************************************/
const _rememberBlocks = () => {
  timeTravel.history = timeTravel.history
    .concat([
      {
        blocks: mobx.toJS(blocks),
        selected: store.selected
      }
    ])
    .splice(-20);
};

const rememberBlocks = _.debounce(_rememberBlocks, 250, {
  leading: true,
  trailing: false
});

const undoRedo = (from, to) => {
  const lasthist = timeTravel[from].slice(-1)[0];
  if (lasthist) {
    timeTravel[from] = timeTravel[from].slice(0, -1);
    timeTravel[to] = timeTravel[to].concat([
      {
        blocks: mobx.toJS(blocks),
        selected: store.selected
      }
    ]);
    blocks.merge(lasthist.blocks);
    store.selected = lasthist.selected;
  }
};

export const undo = mobx.action("undo", () => undoRedo("history", "future"));

export const redo = mobx.action("redo", () => undoRedo("future", "history"));

/****************************** COMPUTED *********************************
 **************************************************************************/

export const getParent = id => blocks.get(blocks.get(id).parent);
export const positionIndex = id => (getParent(id) ? getParent(id).children.indexOf(id) : 0);
export const isCanvasSelected = () => store.selected === "canvas";
export const selectedBlock = () => blocks.get(store.selected);
export const getBlocks = mobx.computed(() => mobx.toJS(blocks));
export const getTree = mobx.computed(() =>
  R.map(R.pick(["id", "label", "children", "type"]), mobx.toJS(blocks))
);

export const getSelected = mobx.computed(() => store.selected);

export const block = id => blocks.get(id);

export const isPreview = mobx.computed(() => store.preview);

export const descendants = (blocks, id) =>
  blocks[id].children.reduce((acc, c) => acc.concat([c], descendants(blocks, c)), []);

const ascendants_ = (blocks, id, acc) => {
  return id === undefined
    ? acc
    : ascendants_(blocks, blocks.get(id).parent, [id].concat(acc || []));
};

export const ascendants = id => ascendants_(blocks, id, []);

const parentWithType = (id, parentType) =>
  getParent(id) && getParent(id).type === parentType && getParent(id);

const parentWithTypeDeep = (id, parentType) =>
  parentWithType(id, parentType) ||
  (block(id).parent && parentWithTypeDeep(block(id).parent, parentType));

const childWithChild = (parentId, childId) =>
  block(parentId).children.filter(cid => cid === childId)[0];
const childWithChildDeep = (parentId, childId) =>
  childWithChild(parentId, childId) ||
  block(parentId).children.filter(cid => childWithChildDeep(cid, childId))[0];

const siblingIndex = id => {
  const siblings = getParent(id).children;
  return siblings.indexOf(id);
};

export const blocksJS = blocks => mobx.toJS(blocks);

/****************************** ACTIONS *********************************
 ***************************************************************************/
const persisBlocks = () => {
  syncBlocks(resizeBlocks(mobx.toJS(blocks), store.canvasSize.width, true));
};

export const duplicateCurrentScene = () => {
  const changeLabel = mapPath(["canvas", "label"], l => l + "Copy", mobx.toJS(blocks));
  createScene(user.id, resizeBlocks(changeLabel, store.canvasSize.width, true));
};

export const printCurrentScene = () => {
  console.log(resizeBlocks(mobx.toJS(blocks), store.canvasSize.width, true));
};

export const resizeBlock = (b, scale, isSaving) => {
  const props = [
    "width",
    "height",
    "top",
    "left",
    "fontSize",
    "borderWidth",
    "space",
    "perspective"
  ];
  const newRec = props.reduce(
    (acc, p) => ({
      ...acc,
      ...(b[p] && _.isNumber(b[p]) && { [p]: isSaving ? b[p] / scale : b[p] * scale })
    }),
    {}
  );
  return Object.assign({}, b, b.id !== "canvas" && newRec);
};

export const resizeBlocks = (blocks, scale, isSaving) =>
  _.mapValues(blocks, b => resizeBlock(b, scale, isSaving));

const makeBlock = (givenId, blocks, type, top, left, properties, parent, label) => {
  const id = givenId || type + uuid();
  const cIndex = blocks[parent].children.filter(c => blocks[c].type === type).length;
  const newBlocks = Object.assign({}, blocks, {
    [parent]: mapPath(["children"], cs => [...cs, id], blocks[parent]),
    [id]: {
      ...properties,
      left: Math.round(left),
      top: Math.round(top),
      type: type,
      id: id,
      children: [],
      rules: [],
      parent: parent || "canvas",
      label: properties.label || label + (cIndex === 0 ? "" : cIndex)
    }
  });
  return (properties.children || []).reduce(
    (acc, c, idx) =>
      makeBlock(undefined, acc, c.type, c.top, c.left, c, id, c.label || Labels[c.type]),
    newBlocks
  );
};

const checkAspect = (id, properties, block) => {
  const { width = block.width, height = block.height } = properties;
  if (!block.lockAspectRatio) return { width: Math.round(width), height: Math.round(height) };
  if (height <= 0 || width <= 0) return { width: block.width, height: block.height };
  const ratio = block.width / block.height;
  const delta =
    Math.abs(width - block.width) > Math.abs(height - block.height)
      ? { width: width, height: width / ratio }
      : { height: height, width: height * ratio };
  return delta;
};

const checkOrientation = (id, properties) =>
  properties.orientation && {
    width: block(id).height,
    height: block(id).width
  };

const moveZIndex = (id, fn) => {
  blocks.set(
    getParent(id).id,
    mapPath(["children"], cs => fn(cs.filter(c => c !== id)), getParent(id))
  );
};

export const bringBlockToFront = mobx.action("bringBlockFront", id =>
  moveZIndex(id, cs => cs.concat(id))
);

export const sendBlockToBack = mobx.action("sendBack", id => moveZIndex(id, cs => [id].concat(cs)));

export const selectBlock = mobx.action("selectBlock", id => {
  store.selected = id;
  const parentSlide = parentWithTypeDeep(id, ItemTypes.ELEMENT_SLIDER);
  if (parentSlide) {
    const slideBlockId = childWithChildDeep(parentSlide.id, id);
    const slideNumber = siblingIndex(slideBlockId) + 1;
    updateProperties(parentSlide.id, { slide: slideNumber });
  }
});

export const updateProperties = mobx.action("updateProps", (id, properties) => {
  rememberBlocks();
  const withConstraints = {
    ...properties,
    // ...(this.state.constrained && this.checkBound(id, properties)),
    ...checkAspect(id, properties, block(id)),
    ...checkOrientation(id, properties)
  };

  //consider not debouncing when properties.hidden to show/hide overlays quicklier
  blocks.set(id, Object.assign({}, blocks.get(id), withConstraints));

  if (properties.zIndex !== undefined) {
    moveZIndex(id, cs => cs.slice(0, properties.zIndex).concat(id, cs.slice(properties.zIndex)));
  }

  if (properties.fontFamily) {
    loadGoogleFonts([properties.fontFamily]);
  }

  if (properties.url) {
    getAspectRatio(properties.url, ratio => {
      block(id).height = block(id).width / ratio;
      persisBlocks();
    });
  } else {
    persisBlocks();
  }
  // persisBlocks();
});

export const updateSelected = ps => updateProperties(store.selected, ps);

export const addBlock = mobx.action(
  "addBlock",
  (type, top, left, customProps, parent, opts = {}) => {
    const starter = (customProps && customProps.starter) || type;
    const properties = Object.assign({}, starterProperties(starter), customProps);
    const id = type + uuid();
    //slide are not droppable so if dropping on slider, we actually drop inside the selected slider
    const resolvedParent =
      block(parent).type === ItemTypes.ELEMENT_SLIDER && !opts.isNewSlide
        ? block(parent).children[block(parent).slide - 1]
        : parent;
    const addB = custom => {
      blocks.merge(
        makeBlock(id, mobx.toJS(blocks), type, top, left, custom, resolvedParent, Labels[starter])
      );
      store.selected = id;
      persisBlocks();
    };

    type === ItemTypes.ELEMENT_IMAGE && customProps.resolvedUrl
      ? getAspectRatio(customProps.resolvedUrl, r =>
          addB({ ...properties, height: properties.width / r })
        )
      : addB(properties);
    return id;
  }
);

export const deleteBlock = mobx.action("deleteBlock", id => {
  //delete in parent
  //delete children
  //delete block
  const block = blocks.get(id);
  const parent = getParent(id);
  getParent(id).children = parent.children.filter(c => c !== id);
  store.selected = parent.id;
  blocks.forEach((value, key) => {
    if (block.children.concat([id]).includes(key)) {
      blocks.delete(key);
    }
  });
  persisBlocks();
});

export const addSlide = mobx.action("addSlide", sliderId => {
  const slider = block(sliderId);
  const newBlockId = addBlock(
    ItemTypes.ELEMENT_EMPTYCONTAINER,
    0,
    0,
    starterSlideProperties(slider.width, slider.height, slider.children.length + 1, sliderId),
    sliderId,
    { isNewSlide: true }
  );
  selectBlock(newBlockId);
});

export const goNextSlide = mobx.action("goNextSlide", sliderId => {
  const slider = block(sliderId);
  slider.slide = slider.slide % slider.children.length + 1;
  //so the slider knows to keep moving to the right
  slider.preview = true;
});

export const goNextSkip = mobx.action("goNextSkip", id => {
  block(id)._skip = block(id)._skip + block(id).limit;
});

export const expandRight = id => {
  const parent = getParent(id);
  const parentW = parent.id === "canvas" ? store.canvasSize.width : parent.width;
  const tgtWidth = parentW - block(id).left;
  updateProperties(id, { width: tgtWidth });
};

export const expandLeft = id => {
  const b = block(id);
  updateProperties(id, { left: 0, width: b.width + b.left });
};

export const expandUp = id => {
  const b = block(id);
  updateProperties(id, { top: 0, height: b.height + b.top });
};

export const expandDown = id => {
  const parent = getParent(id);
  const parentH = parent.id === "canvas" ? store.canvasSize.height : parent.height;
  const tgtHeight = parentH - block(id).top;
  updateProperties(id, { height: tgtHeight });
};

export const moveDownFull = id => {
  const parent = getParent(id);
  const parentH = parent.id === "canvas" ? store.canvasSize.height : parent.height;
  updateProperties(id, { top: parentH - block(id).height });
};

export const moveRightFull = id => {
  const parent = getParent(id);
  const parentW = parent.id === "canvas" ? store.canvasSize.width : parent.width;
  updateProperties(id, { left: parentW - block(id).width });
};

const deepRefChildren = p =>
  Object.assign({}, p, {
    children: p.children.map(cid => deepRefChildren(blocks.get(cid)))
  });

export const duplicateBlock = mobx.action("duplicate", id => {
  const original = blocks.get(id);
  const withChildren = deepRefChildren(original);
  addBlock(original.type, original.top + 40, original.left + 40, withChildren, original.parent);
});

var clipboard = undefined;

export const copyBlock = mobx.action("copy", id => {
  const original = blocks.get(id);
  const withChildren = deepRefChildren(original);
  clipboard = withChildren;
});

export const cutBlock = mobx.action("cut", id => {
  copyBlock(id);
  deleteBlock(id);
});

export const pasteBlock = mobx.action("paste", () => {
  const original = clipboard;
  if (original) {
    const parent =
      store.selectedBlock.type === ItemTypes.ELEMENT_EMPTYCONTAINER &&
      store.selected !== original.id
        ? store.selected
        : original.parent;

    const top = store.selected === original.id ? original.top : 0;
    const left = store.selected === original.id ? original.left : 0;
    const newId = addBlock(original.type, top + 20, left + 20, original, parent);
    copyBlock(newId);
  }
});

export const changeBlockParent = mobx.action("changeBlockParent", (id, newparent) => {
  const original = deepRefChildren(blocks.get(id));
  deleteBlock(id);
  addBlock(original.type, 2, 2, original, newparent);
});

export const linkProperty = mobx.action("linkProperty", (id, prop, target) => {
  _.set(blocks.get(id), [prop, "target"], target);
  persisBlocks();
});

export const setPropertyMultiplier = mobx.action("prop multiplier", (id, prop, mult) => {
  _.set(blocks.get(id), [prop, "multiplier"], mult);
  persisBlocks();
});

export const unLinkProperty = mobx.action("unlinkProperty", (id, prop) => {
  blocks.get(id)[prop] = propertyDefault(block(id).type, prop);
  persisBlocks();
});

export const linkTextProperty = mobx.action("linkTextProperty", (id, prop, target) => {
  _.set(blocks.get(id), [prop], "{" + target + "}");
  persisBlocks();
});

export const updateRule = (id, prop, idx, key, v) => {
  _.set(blocks.get(id), ["rules", idx, key], v);
  persisBlocks();
};

export const addRule = mobx.action("addRule", (id, prop) => {
  block(id).rules.push({
    property: prop,
    cell: "twitch.name",
    comparator: RuleComparator.Equal,
    constant: "",
    value: false
  });
});

export const doToSelected = act => () => act(store.selected);

const toggleProp = (id, prop) => updateProperties(id, { [prop]: !blocks.get(id)[prop] });

export const toggleLock = mobx.action("toggle lock", id => toggleProp(id, "locked"));
export const toggleVisibility = mobx.action("toggle visibility", id => toggleProp(id, "hidden"));
export const showBlock = mobx.action("show block", id => updateProperties(id, { hidden: false }));
export const hideBlock = mobx.action("hide block", id => updateProperties(id, { hidden: true }));

export const toggleOutline = mobx.action(
  "toggleOutline",
  () => (store.showOutline = !store.showOutline)
);

export const showPreview = mobx.action("showPreview", () => (store.preview = true));
export const stopPreview = mobx.action("stopPreview", () => (store.preview = false));

export default store;



// WEBPACK FOOTER //
// ./src/canvas/store.js